import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faClipboard,
  faExclamationTriangle,
  faPlus,
  faCalendar,
  faFilter,
  faSun,
  faMap,
  faCircleDot,
  faStopwatch,
  faChair,
  faUsers,
  faCircleXmark,
  faCircleCheck,
  faFilePen,
  faRectangleXmark,
  faEnvelope,
  faWheelchairMove,
  faBaby,
  faReply,
  faSearch,
  faSquareCheck,
  faEye,
  faPhone,
  faXmark,
  faGear,
  faLock,
  faPhoneSlash,
  faPaperPlane,
  faComments,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Card, Button, Modal } from "react-bootstrap";
import {
  fetchAllReservations,
  postReservationAccept,
} from "../actions/restaurantActions";
import {
  fetchCancelReservation,
  fetchReservationShift,
  fetchReservationBlockedWeekdays,
  checkCancelAction,
} from "../actions/reservationsActions";
import Messages from "../components/Messages";
import Loader from "../components/Loader";
import ReservationChat from "../components/ReservationChat";
import ReservationScreen from "../screens/ReservationScreen";
import CompareChanges from "./CompareChanges";
import ReservationStatusBox from "./ReservationStatusBox";
import { cancelledStatuses } from "./ReservationStatus";
import ShowCustomerInfo from "./ShowCustomerInfo";
import CustomSelect from "../components/SelectableCustom";
import {
  CalendarSvgIcon,
  FilterSvgIcon,
  GearSvgIcon,
  NoPhoneSvgIcon,
  OnOffSvgIcon,
  PhoneSvgIcon,
  PlusSvgIcon,
  ReservationSvgIcon,
  TableSvgIcon,
  UsersSvgIcon,
  XSvgIcon,
  UserSvgIcon,
  ClockSvgIcon,
  BellSvgIcon,
  BubbleTextSvgIcon,
  ZoneSvgIcon,
  PennotebookSvgIcon,
  ExtrasSvgIcon,
} from "../icons/SvgIcons";

import { CardHeader, CardHeaderTitle } from "../components/DefaultImports";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import CollapsibleChat from "./chat/CollapsibleChat";
import ReservationCard from "./ReservationCard";

const slideIn = keyframes`
  from {
    transform: translateX(100%); /* Start from the right */
  }
  to {
    transform: translateX(0); /* Slide to original position */
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0); /* Start from original position */
  }
  to {
    transform: translateX(100%); /* Slide to the right */
  }
`;

const BarContainer = styled.div`
  width: 100vw; /* Viewport width */
  height: 100%; /* Viewport height */
  position: fixed;
  top: 0; /* Position from the top */
  left: 0; /* Position from the left */
  z-index: 1000; /* High z-index to stay on top */
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.2);

  /* Conditional animations */
  animation: ${(props) => (props.clicked ? slideIn : slideOut)} 0.5s forwards;
`;

const InsideContainerBox = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ee754c;
  overflow: hidden;
  display: flex;
  justify-content: end;
`;

const InsideContainer = styled.div`
  padding: 30px 5px 5px 10px;
  position: relative;
  width: 98%;
  background-color: white;
  height: 100%;
  overflow: hidden;

  /* Estilização da scrollbar */
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #e20202ff;
    &:hover {
      background-color: #bbbbbb;
    }
  }

  &::-webkit-scrollbar {
    width: 12px;
    background-color: transparent;
  }

  /* Media query para ecrãs pequenos */
  @media (max-width: 768px) {
    padding-top: 40px; /* Aumenta 10px no padding-top */
    padding-top: 10px; /* Ou usa margin-top para descer o container */

    /* Opcional: ajustar outros elementos para mobile */
    padding-left: 15px;
    padding-right: 15px;
  }

  /* Media query para ecrãs muito pequenos */
  @media (max-width: 480px) {
    padding-top: 50px; /* Pode ajustar ainda mais se necessário */
  }
`;

const orbit = (start, end) => keyframes`
  from {
    transform: rotate(${start}deg);
  }
  to {
    transform: rotate(${end}deg);
  }
`;

const CircleContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: fixed;
  top: 15px;
  right: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #98afad;
  cursor: pointer;
  padding: 2px;
  z-index: 1001;

  @media (max-width: 768px) {
    position: fixed;
    top: 10px;
    right: 50px;
  }
`;

const NotificationCircle = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  bottom: 20px; /* Half the size of the small circle */
  right: 20px; /* Half the size of the small circle */

  /* This wrapper helps keep the number inside the circle straight while the circle itself rotates. */
  & > div {
    display: flex;
    justify-content: center;
    align-items: center; /* Counter the initial 45deg rotation of the parent */
  }

  ${({ animationDegrees, animate }) =>
    animate &&
    css`
      transform-origin: 29px 27px; /* The center of the main circle */
      animation: ${orbit(animationDegrees.start, animationDegrees.end)} 0.5s
        forwards;
    `}
`;

const YellowCircle = styled(NotificationCircle)`
  color: white;
  background: #ee754c;
`;

const RedCircle = styled(NotificationCircle)`
  color: white;
  background: red;
`;

const tremble = keyframes`
0% { transform: rotate(0deg); }
25% { transform: rotate(-6deg); }
75% { transform: rotate(6deg); }
100% { transform: rotate(0deg); }
`;

const AnimatedIcon = styled(FontAwesomeIcon)`
  color: #5fddb3;
  font-size: 60px;
  ${({ animate }) =>
    animate &&
    css`
      animation: ${tremble} 0.2s ease-in-out 3; /* Adjust time and iteration count as needed */
    `}
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 10px;
  height: 10px;
  background-color: #ee754c;
  border-radius: 50%;
  border: 1px solid white;
`;

function NotificationBell({
  restaurant_id,
  allReservations,
  reservationsWithChatMessages,
}) {
  const [activeKey, setActiveKey] = useState("reservas");

  const userInfoState = useSelector((state) => state.userLogin);
  const { userInfo, isLoading } = userInfoState;

  const reservationAccept = useSelector((state) => state.reservationAccept);
  const { messageAccept, isLoadingAccept } = reservationAccept;

  const reservationCancel = useSelector((state) => state.cancelReservation);
  const { cancelMessage, isLoadingCancel } = reservationCancel;

  const all_tables = useSelector((state) => state.restaurantTables.tables);

  const manager_info = useSelector((state) => state.crudManager);
  const { managerInfo } = manager_info;

  const modalRef = useRef(null);
  const modalPhoneRef = useRef(null);
  const modalCancelRef = useRef(null);
  const notificationBellRef = useRef(null);
  const editModalRef = useRef(null);
  const statusBoxRef = useRef(null);
  const colRef = useRef(null); // Step 1: Create a reference

  const dispatch = useDispatch();
  const [clicked, setClicked] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [tokens, setTokens] = useState("");
  const [reservationForChat, setReservationForChat] = useState("");
  const [chatModalTrueOrFalse, setChatModalTrueOrFalse] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [divChatHeight, setDivChatHeight] = useState(3000);

  const [reservationHasBeenModified, setReservationHasBeenModified] =
    useState(false);
  const [
    modalReservationModifyReservation,
    setModalReservationModifyReservation,
  ] = useState(false);

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [completePhone, setCompletePhone] = useState("");

  const [pendingNumber, setPendingNumber] = useState(0);
  const [cancelledNumber, setCancelledNumber] = useState(0);

  const [
    allReservationsWithDateSeparators,
    setAllReservationsWithDateSeparators,
  ] = useState([]);
  const [reset, setReset] = useState(false);

  const contentRef = useRef(null);

  const [hasUnreadChatNotifications, setHasUnreadChatNotifications] =
    useState(false);

  const [hasNewReservation, setHasNewReservation] = useState(false);

  useEffect(() => {
    if (allReservations) {
      // Verifica se há mensagens não lidas no chat
      const hasUnread = allReservations.some((reservation) => {
        const notifications =
          typeof reservation.chat_notification_restaurant === "string"
            ? reservation.chat_notification_restaurant.split(",")
            : [];
        return (
          (reservation.chat_notification_restaurant_all ||
            (reservation.chat_notification_restaurant &&
              notifications.includes(userInfo.name))) &&
          reservation.chat_has_messages
        );
      });
      setHasUnreadChatNotifications(hasUnread);

      const hasNew = allReservations.some(
        (reservation) =>
          reservation.status === "Pending_from_restaurant" ||
          reservation.status === "Edited" ||
          reservation.status === "Waiting_List" ||
          reservation.cancelled_warning === true
      );
      setHasNewReservation(hasNew);
    }
  }, [allReservations, userInfo.name]);

  // Scroll to top handler
  const handleScrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Modified to scroll up when tab changes
  const handleTabSelect = (key) => {
    setActiveKey(key);
    handleScrollToTop();
  };

  const freezeScroll = () => {
    contentRef.current.style.overflowY = "hidden";
  };

  const unfreezeScroll = () => {
    contentRef.current.style.overflowY = "auto";
  };

  const handleChatToggle = () => {};

  const stableRestaurantId = useMemo(() => restaurant_id, [restaurant_id]);

  useEffect(() => {
    if (stableRestaurantId) {
      dispatch(
        fetchAllReservations(
          stableRestaurantId,
          null,
          null,
          null,
          "all_pending_reservations_from_all_time"
        )
      );
    }
  }, [dispatch, stableRestaurantId]);
  useEffect(() => {
    if (allReservations) {
      // Calculate the cancelled number
      const cancelledCount = allReservations.filter(
        (reservation) => reservation.cancelled_warning === true
      ).length;
      // Calculate the pending number
      const pendingCount = allReservations.filter(
        (reservation) =>
          reservation.status === "Cancelled_By_Customer_Warning" ||
          reservation.status === "Pending_from_restaurant" ||
          reservation.status === "new_chat_message" ||
          reservation.status === "Edited" ||
          reservation.chat_notification_restaurant_all
      ).length;
      // Set the state
      setCancelledNumber(cancelledCount);
      setPendingNumber(pendingCount);
      console.log("allReservations inside useffect: ", allReservations);
      console.log(pendingCount);
    }
  }, [allReservations]);

  useEffect(() => {
    if (allReservations && allReservations.length > 0) {
      // Make sure your dates are sorted, if they're not already.
      // This sort function assumes your dates are in 'YYYY-MM-DD' format and are strings.
      const sortedReservations = [...allReservations].sort((a, b) =>
        a.date.localeCompare(b.date)
      );

      const reservationsWithSeparators = [];
      let lastDate = null;

      sortedReservations.forEach((reservation) => {
        // If this reservation is on a different day, add a date separator.
        if (reservation.date !== lastDate) {
          reservationsWithSeparators.push({
            isDateSeparator: true,
            date: reservation.date,
          });
          lastDate = reservation.date;
        }
        // Add the regular reservation.
        reservationsWithSeparators.push(reservation);
      });

      setAllReservationsWithDateSeparators(reservationsWithSeparators);
    }
  }, [allReservations]);

  // Define the start and end points for the animation
  const animationDegreesYellow = clicked
    ? { start: 180, end: 0 } // Clicked: Go to the other side
    : { start: 0, end: 180 }; // Unclicked: Return to the original position
  const animationDegreesRed = clicked
    ? { start: 140, end: -40 } // Clicked: Go to the other side
    : { start: -40, end: 140 }; // Unclicked: Return to the original position

  const counterRotate = clicked ? "rotate(0deg)" : "rotate(-180deg)";
  const counterRotateRed = clicked ? "rotate(40deg)" : "rotate(-140deg)";

  /* const animationDegreesYellow = clicked
  ? { start: 180, end: 0 } // Clicked: Go to the other side
  : { start: 0, end: 180 }; // Unclicked: Return to the original position
  const animationDegreesRed = clicked
  ? { start: 140, end: -40 } // Clicked: Go to the other side
  : { start: -40, end: 140 }; */

  // New function to handle click
  const handleClick = () => {
    setAnimate(true);
    if (!clicked) {
      setClicked(true);
    } else {
      setClicked(false);
    }
    // Timeout duration should match your animation duration
    setTimeout(() => {
      setAnimate(false);
    }, 200); // this should match the duration of your tremble animation
  };

  const handleClickOutside = (event) => {
    // Check if the clicked target is the modal, notification bell, or their descendants
    if (
      (notificationBellRef.current &&
        notificationBellRef.current.contains(event.target)) ||
      (editModalRef.current && editModalRef.current.contains(event.target)) ||
      (modalCancelRef.current &&
        modalCancelRef.current.contains(event.target)) ||
      (modalPhoneRef.current && modalPhoneRef.current.contains(event.target)) ||
      (statusBoxRef.current && statusBoxRef.current.contains(event.target)) ||
      (modalRef.current && modalRef.current.contains(event.target))
    ) {
      // Clicked inside the modal or on the notification bell, ignore the click
      return;
    }
    if (colRef.current && !colRef.current.contains(event.target)) {
      setClicked(false);
    }
  };

  useEffect(() => {
    // Step 3: Add event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <CircleContainer ref={notificationBellRef} onClick={handleClick}>
        <AnimatedIcon
          animate={animate}
          icon={faBell}
          style={{ color: "#ffffff", fontSize: "30px" }}
        />
        {pendingNumber > 0 && (
          <YellowCircle
            animate={true}
            animationDegrees={animationDegreesYellow}
          >
            <div style={{ transform: counterRotate }}>
              {pendingNumber < 10 ? pendingNumber : "9+"}
            </div>{" "}
            {/* Wrapping the number to keep it straight */}
          </YellowCircle>
        )}
        {cancelledNumber > 0 && (
          <RedCircle animate={true} animationDegrees={animationDegreesRed}>
            <div style={{ transform: counterRotateRed }}>
              {cancelledNumber < 10 ? cancelledNumber : "9+"}
            </div>{" "}
            {/* Wrapping the number to keep it straight */}
          </RedCircle>
        )}
      </CircleContainer>
      <BarContainer clicked={clicked}>
        <Row
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "end",
            padding: 0,
            margin: "0",
          }}
        >
          <Col
            ref={colRef}
            xs={12}
            sm={12}
            md={6}
            xl={4}
            style={{
              color: "black",
              height: "100%",
              padding: 0,
              margin: "0",
            }}
          >
            {}
            <InsideContainerBox>
              {/* HERE START OUR SUPER NICE SLIDE BAR */}
              <div className="menu-left"></div>
              <InsideContainer>
                <div style={{ position: "relative", height: "100%" }}>
                  <Tabs
                    activeKey={activeKey}
                    onSelect={handleTabSelect}
                    defaultActiveKey="reservas"
                    id="uncontrolled-tab-example"
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1000,
                    }}
                  >
                    <Tab
                      eventKey="reservas"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Reservas</b>
                          <FontAwesomeIcon
                            icon={faClipboard}
                            style={{
                              marginLeft: "5px",
                              color: "inherit",
                            }}
                          />
                          {hasNewReservation && <NotificationDot />}
                        </>
                      }
                    />
                    <Tab
                      eventKey="mensagens"
                      title={
                        <>
                          <b style={{ color: "inherit" }}>Mensagens</b>
                          <FontAwesomeIcon
                            icon={faComments}
                            style={{ marginLeft: "5px", color: "inherit" }}
                          />
                          {hasUnreadChatNotifications && <NotificationDot />}
                        </>
                      }
                    />
                  </Tabs>

                  <div
                    ref={contentRef}
                    style={{
                      position: "absolute",
                      top: "65px", // Start content 100px from top
                      bottom: 0,
                      left: 0,
                      right: 0,
                      width: "100%",
                      overflowY: "auto",
                      overflowBehaviour: "contain",
                    }}
                  >
                    {activeKey === "reservas" ? (
                      <div
                        style={{
                          overflowBehaviour: "contain",
                          width: "100%",
                        }}
                      >
                        {allReservations && allReservations.length > 0 ? (
                          <ReservationCard
                            style={{
                              width: "100%",
                            }}
                            reservations={allReservations.filter(
                              (reservation) =>
                                reservation.status ===
                                  "Pending_from_restaurant" ||
                                reservation.status === "Edited" ||
                                reservation.status === "Waiting_List" ||
                                reservation.cancelled_warning === true
                            )}
                            isNotifications={true}
                          />
                        ) : allReservations && allReservations.length === 0 ? (
                          <p>Sem novas reservas.</p>
                        ) : (
                          <p>Nothing to be found</p>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "15px",
                          overflowBehaviour: "contain",
                        }}
                      >
                        {allReservations &&
                          allReservations
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ","
                                    )
                                  : [];

                              return (
                                (reservation.chat_notification_restaurant_all ||
                                  (reservation.chat_notification_restaurant &&
                                    notifications.includes(userInfo.name))) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={true}
                                onToggle={handleChatToggle}
                              />
                            ))}

                        {reservationsWithChatMessages &&
                          reservationsWithChatMessages
                            .filter((reservation) => {
                              const notifications =
                                typeof reservation.chat_notification_restaurant ===
                                "string"
                                  ? reservation.chat_notification_restaurant.split(
                                      ","
                                    )
                                  : [];

                              return (
                                !reservation.chat_notification_restaurant_all &&
                                (!reservation.chat_notification_restaurant ||
                                  !notifications.includes(userInfo.name)) &&
                                reservation.chat_has_messages
                              );
                            })
                            .map((reservation) => (
                              <CollapsibleChat
                                key={reservation.id}
                                reservation={reservation}
                                isClient={false}
                                style={{ width: "100%" }}
                                isNewMessage={false}
                                onToggle={handleChatToggle}
                              />
                            ))}
                      </div>
                    )}
                  </div>
                </div>
              </InsideContainer>
            </InsideContainerBox>
          </Col>
        </Row>
      </BarContainer>
      {/*
      <Modal show={showAcceptModal} onHide={handleCloseAcceptModal}>
        <div ref={modalRef}>
          <Modal.Header closeButton>
            <Modal.Title>Aceitar Reserva</Modal.Title>
          </Modal.Header>
          <Modal.Footer>
            {isLoadingAccept ? (
              <Loader></Loader>
            ) : (
              <Button variant="success" onClick={() => handleAccept()}>
                Sim
              </Button>
            )}
            <Button variant="secondary" onClick={handleCloseAcceptModal}>
              Não
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal show={showModal} onHide={handleCloseModal}>
        <div ref={modalCancelRef}>
          <Modal.Header closeButton>
            <Modal.Title>Rejeitar reserva</Modal.Title>
          </Modal.Header>
          <Modal.Footer>
            {isLoadingCancel ? (
              <Loader></Loader>
            ) : (
              <Button variant="success" onClick={handleCancel}>
                Sim
              </Button>
            )}
            <Button variant="danger" onClick={handleCloseModal}>
              Não
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {isReservationModalOpen && (
        <Modal
          show={isReservationModalOpen}
          onHide={() => {
            if (reservationHasBeenModified) {
              setModalReservationModifyReservation(true);
            } else {
              setIsReservationModalOpen(false);
            }
          }}
        >
          <Modal.Header closeButton></Modal.Header>

          <ReservationScreen
            from_restaurant={true}
            creating={isCreatingReservation}
            setCreating={(value) => setIsCreatingReservation(value)}
            isEditModalOpen={isReservationModalOpen}
            setIsEditModalOpen={setIsReservationModalOpen}
            restaurantId={restaurant_id}
            handleCloseReservationModal={handleCloseReservationModal}
            token_from_restaurant={tokens}
            setTokens={setTokens}
            reset={reset}
            setReservationHasBeenModified={setReservationHasBeenModified}
            reservationHasBeenModified={reservationHasBeenModified}
            modalReservationModifyReservation={
              modalReservationModifyReservation
            }
            setModalReservationModifyReservation={
              setModalReservationModifyReservation
            }
          />
        </Modal>
      )}

      <Modal
        show={showPhoneModal}
        onHide={() => setShowPhoneModal(!showPhoneModal)}
      >
        <div ref={modalPhoneRef}>
          <Modal.Header>
            <div style={{ fontSize: "20px" }}>+{completePhone}</div>
          </Modal.Header>
          <Modal.Footer>
            <a
              href={`tel:${"+" + completePhone}`}
              style={{ cursor: "pointer" }}
            >
              <Button style={{ backgroundColor: "#3bad59" }}>
                Ligar
                <FontAwesomeIcon
                  size="lg"
                  icon={faPhone}
                  style={{ marginLeft: "7px" }}
                />
              </Button>
            </a>

            <Button
              style={{ backgroundColor: "red" }}
              onClick={() => setShowPhoneModal(!showPhoneModal)}
            >
              Fechar
              <FontAwesomeIcon
                size="lg"
                icon={faXmark}
                style={{ marginLeft: "7px" }}
              />
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
      <ReservationChat
        reservation={reservationForChat}
        isClient={false}
        hideButton={true}
        openModal={chatModalTrueOrFalse}
        setChatModalTrueOrFalse={setChatModalTrueOrFalse}
      /> */}
    </>
  );
}

export default NotificationBell;
